import SelectDate from "@/components/selectDate";
import useSelectDate from "@/hooks/useSelectDate";
import {
  detailEventService,
  updateEventService,
  uploadBannerService,
} from "@/services/event.service";
import { CreateEventForm, DestinationItem } from "@/types";
import { ROUTE } from "@/types/router";
import { cn } from "@/utils/utils";
import { CircularProgress, Theme, useMediaQuery } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SelectLocationCreateEvent from "@/components/create-event/select-location";
import SelectHotels from "@/components/create-event/select-hotels";
import { IconButton } from "@mui/material";
import TextEditor from "@/components/ui/text-editor";
import SelectAirport from "@/components/select-airport";

const baseInputClassName =
  "placeholder:text-[#94A3B8] text-sm h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal";

const initialValues: CreateEventForm = {
  name: "",
  description: "",
  startTime: "",
  endTime: "",
  location: "",
  locationId: "",
  banner: "",
  latitude: "",
  longitude: "",
  featuredHotels: [],
  active: true,
  link: "",
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Please enter title!")
    .test(
      "is-not-empty",
      "Please enter a valid title!",
      (value) => value?.trim().length > 0,
    ),
  description: Yup.string()
    .required("Please enter description!")
    .test(
      "is-not-empty",
      "Please enter a valid description!",
      (value) => value?.trim().length > 0,
    ),
  link: Yup.string()
    .required("Please enter event link!")
    .test(
      "is-not-empty",
      "Please enter a valid link!",
      (value) => value?.trim().length > 0,
    ),
  startTime: Yup.string().required("Please select event start time!"),
  endTime: Yup.string().required("Please select event end time!"),
  location: Yup.string().required("Please select event location!"),
  // locationId: Yup.string().required("Please select event location!"),
  banner: Yup.string().required("Please upload banner!"),
  featuredHotels: Yup.array()
    .min(1, "Please select hotels")
    .of(Yup.string().required("Please select hotels")),
  suggestedAirports: Yup.string().optional(),
});

const EditEventPage = () => {
  const { id } = useParams();
  const { date, handleOpenUserMenu, ...propsDate } = useSelectDate();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isLoadingUpload, setLoadingUpload] = useState<boolean>(false);
  const refForm = useRef<CreateEventForm>(initialValues);
  const navigation = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const { data: dataEvent, refetch } = useQuery({
    queryKey: ["detailEventService", id],
    queryFn: () => detailEventService(id || ""),
    enabled: !!id,
  });

  const handleUpdateEvent = useCallback(
    async (formData: CreateEventForm) => {
      try {
        setLoading(true);
        await updateEventService(
          {
            ...formData,
            startTime: dayjs(formData.startTime).toISOString(),
            endTime: dayjs(
              new Date(formData.endTime).setHours(23, 59, 59, 999),
            ).toISOString(),
            featuredHotels: formData.featuredHotels.map((item) => ({
              id: item,
              price: 0,
            })),
          },
          id || "",
        );
        refetch();
        toast.success("Event successfully updated");
        window.scrollTo({ top: 0, behavior: "smooth" });
        navigation(ROUTE.Home, { replace: true });
      } catch (error) {
        toast.error("Update event failed!");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  const {
    values,
    handleChange,
    submitForm,
    errors,
    setValues,
    setFieldValue,
    validateField,
  } = useFormik<CreateEventForm>({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: handleUpdateEvent,
  });

  const handleFile = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    if (acceptedFiles.length === 0) {
      return toast.error("Please upload only one image");
    }

    if (
      acceptedFiles[0].type !== "image/png" &&
      acceptedFiles[0].type !== "image/jpeg"
    ) {
      return toast.error("File type must be PNG or JPG");
    }

    try {
      setLoadingUpload(true);
      const bannerLink = await uploadBannerService(acceptedFiles[0]);
      setFieldValue("banner", bannerLink);
    } catch (error) {
      toast.error("'Upload banner failed!");
    } finally {
      setLoadingUpload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFile,
    maxFiles: 1,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/webp": [],
      "image/gif": [],
    },
  });

  useEffect(() => {
    if (dataEvent) {
      setValues({
        name: dataEvent.name || "",
        description: dataEvent.description || "",
        startTime: dataEvent.startTime || "",
        endTime: dataEvent.endTime || "",
        location: dataEvent.location || "",
        locationId: dataEvent.locationId || "",
        banner: dataEvent.banner || "",
        latitude: dataEvent.latitude || "",
        longitude: dataEvent.longitude || "",
        featuredHotels: dataEvent.featuredHotels || [],
        active: dataEvent.active,
        link: dataEvent.link || "",
        suggestedAirports: dataEvent.suggestedAirports || "",
      });
      propsDate.setDate({
        from: new Date(dataEvent.startTime),
        to: new Date(dataEvent.endTime),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEvent]);

  useEffect(() => {
    if (date?.from && date?.to) {
      setValues({
        ...values,
        startTime: date?.from?.toISOString() || "",
        endTime: date?.to?.toISOString() || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date?.from, date?.to]);

  useEffect(() => {
    if (dataEvent) {
      if (refForm.current.name !== values.name) {
        validateField("name");
      }
      if (refForm.current.description !== values.description) {
        validateField("description");
      }
      if (refForm.current.startTime !== values.startTime) {
        validateField("startTime");
      }
      if (refForm.current.link !== values.link) {
        validateField("link");
      }
      if (refForm.current.endTime !== values.endTime) {
        validateField("endTime");
      }
      if (refForm.current.location !== values.location) {
        validateField("location");
      }
      if (
        refForm.current.featuredHotels.length !== values.featuredHotels.length
      ) {
        validateField("featuredHotels");
      }
      if (refForm.current.banner !== values.banner) {
        validateField("banner");
      }
    }

    refForm.current = values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const handleChangeHotel = (hotels: string[]) => {
    setFieldValue("featuredHotels", hotels);
  };

  const handleSelectLocation = (destination: DestinationItem | null) => {
    destination &&
      setValues({
        ...values,
        location: destination.destinationName[0].name,
        locationId: destination.destinationId,
        longitude: destination.destinationName[0].lngGoogle.toString(),
        latitude: destination.destinationName[0].latGoogle.toString(),
      });
  };

  const handleChangeAirport = (airport: string) => {
    setValues({
      ...values,
      suggestedAirports: airport,
    });
  };

  return (
    <div>
      <div className="mb-[14px] flex items-center gap-4">
        <IconButton
          aria-label="back"
          size="medium"
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            height: "40px",
            width: "40px",
          }}
          onClick={() => navigation(ROUTE.Home)}
        >
          <img src="/icons/arrow-left.svg" alt="arrow-left" />
        </IconButton>

        <h2 className="text-lg font-semibold text-text ">Update Event</h2>
      </div>
      <div className="flex flex-col space-y-8 rounded-3xl bg-white p-5 text-[#1E293B] lg:px-8 lg:py-6">
        <div className="flex w-full flex-col">
          <label htmlFor="title" className="mb-2 text-sm font-medium ">
            Title
          </label>
          <input
            id="title"
            type="text"
            name="name"
            aria-label="empty textarea"
            placeholder="Enter title of event"
            className={cn(baseInputClassName)}
            value={values.name}
            onChange={handleChange}
          />
          {errors.name && (
            <p className="mx-1 mt-1 text-[13px] text-red-500">{errors.name}</p>
          )}
        </div>

        <div className="flex w-full flex-col">
          <label htmlFor="description" className="mb-2 text-sm font-medium">
            Description
          </label>

          <TextEditor
            aria-label="empty textarea"
            value={values.description}
            onChange={(value) => setFieldValue("description", value)}
          />
          {/* <textarea
            id="description"
            aria-label="empty textarea"
            name="description"
            placeholder="Enter description of event..."
            className={cn(baseInputClassName, "min-h-[103px] p-3")}
            maxLength={300}
            value={values.description}
            onChange={handleChange}
            rows={40}
          /> */}
          {/* <div className="mt-3 flex items-center justify-between">
            <p className="text-sm font-normal text-[#64748B]">
              Max 300 characters
            </p>
            <p className="text-sm font-normal text-[#64748B]">
              {values.description.length}/300
            </p>
          </div> */}
          {errors.description && (
            <p className="mt-1 text-[13px] text-red-500">
              {errors.description}
            </p>
          )}
        </div>

        <div className="flex w-full flex-col">
          <label htmlFor="link" className="mb-2 text-sm font-medium">
            Event Link
          </label>
          <input
            name="link"
            placeholder="https://"
            className={cn(baseInputClassName)}
            value={values.link}
            onChange={handleChange}
          />
          {errors.link && (
            <p className="mx-1 mt-1 text-[13px] text-red-500">{errors.link}</p>
          )}
        </div>

        <div className="flex w-full flex-col">
          <p className="mb-2 text-sm font-medium">Select Date</p>
          <SelectDate
            {...propsDate}
            date={date}
            styleMenu={{ mt: "54px" }}
            numberOfMonths={isMobile ? 1 : 2}
            contentStyle={{
              width: isMobile ? "var(--radix-popover-trigger-width)" : "",
            }}
            anchor={
              <div
                onClick={
                  propsDate.anchorElUser
                    ? propsDate.handleCloseUserMenu
                    : handleOpenUserMenu
                }
                className="flex h-[48px] w-full items-center justify-between rounded-lg border border-[#E2E8F0] px-3 text-sm"
              >
                <p className={cn("font-normal text-[#020617]")}>
                  {!date?.from && !date?.to ? (
                    <span className="text-neutral-400">
                      Select event duration
                    </span>
                  ) : (
                    `${date?.from ? format(date.from, "yyyy/MM/dd") : ""} - ${
                      date?.to ? format(date.to, "yyyy/MM/dd") : ""
                    }`
                  )}
                </p>
                <KeyboardArrowDownIcon
                  className=""
                  sx={{
                    color: "#94A3B8",
                  }}
                />
              </div>
            }
          />
          {(errors.startTime || errors.endTime) && (
            <p className="mx-1 mt-1 text-[13px] text-red-500">
              {errors.startTime || errors.endTime}
            </p>
          )}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Select Location</p>
          <SelectLocationCreateEvent
            value={values.locationId}
            name={values.location}
            onChange={handleSelectLocation}
          />
          {(errors.location || errors.locationId) && (
            <p className="mx-1 mt-1 text-[13px] text-red-500">
              {errors.location || errors.locationId}
            </p>
          )}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Select Hotels</p>
          <SelectHotels
            values={values.featuredHotels}
            onChange={handleChangeHotel}
          />
          {errors.featuredHotels && (
            <p className="mx-1 mt-1 text-[13px] text-red-500">
              {errors.featuredHotels}
            </p>
          )}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Select Suggested Airport</p>
          <SelectAirport
            airport={values.suggestedAirports || ""}
            onChange={handleChangeAirport}
            placeholder="Select Suggested Airport"
            title="Suggested Airport"
          />
          {errors.suggestedAirports && (
            <p className="mx-1 mt-1 text-[13px] text-red-500">
              {errors.suggestedAirports}
            </p>
          )}
        </div>

        <div className="mb-12">
          <p className="mb-2 text-sm font-medium">Upload banner image</p>
          <div
            className="flex w-full cursor-pointer flex-col rounded-[8px] border border-neutral-200 p-3"
            {...getRootProps()}
          >
            <input
              {...getInputProps()}
              type="file"
              accept="image/png, image/jpeg"
              onClick={(event) => event.stopPropagation()}
            />
            {isLoadingUpload ? (
              <Fragment>
                <CircularProgress
                  size="32px"
                  className="ml-3"
                  color="primary"
                />
              </Fragment>
            ) : values.banner ? (
              <div className="flex flex-col items-end justify-between max-md:gap-3 lg:flex-row lg:items-center">
                <img
                  src={values.banner}
                  className="h-[162px] w-full rounded-[8px] object-cover lg:h-[278px] lg:w-[495px]"
                />
                <button className="h-8 w-[74px] rounded-lg bg-text text-base font-medium text-white outline-none lg:h-12 lg:w-[121px]">
                  Browse
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm font-normal text-neutral-400">
                  Upload supporting image (.JPG, .PNG)
                </p>
              </div>
            )}
          </div>
          <p className="mt-1 text-[12px] text-[#DC2626]">
            The banner image should have a 16:9 or 3:1 aspect ratio and a file
            size of no more than 10MB
          </p>
          {errors.banner && (
            <p className="mt-2 text-[12px] text-red-500">{errors.banner}</p>
          )}
        </div>

        <div className="flex w-full items-center justify-end gap-4">
          <button
            className="h-12 w-[171px] rounded-[10px] bg-button-background text-base font-medium text-text outline-none"
            onClick={() => navigation(ROUTE.Home)}
          >
            Cancel
          </button>
          <button
            onClick={submitForm}
            className="h-12 w-[171px] rounded-[10px] bg-text text-base font-medium text-white outline-none"
            disabled={isLoading || isLoadingUpload}
          >
            Update Event
            {isLoading && (
              <CircularProgress size="16px" className="ml-3" color="inherit" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEventPage;
